import * as React from 'react';

import { Loading } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useSetAtom } from 'jotai';

import { $startEditingSession } from '~/modules/office/_state/officeActions.state';
import {
  $currentEditingSession,
  $isEditorReady,
  $handleCollaboraMessage,
  $sendCollaboraMessage,
  $isDocumentLoaded,
} from '~/modules/office/_state/officeEditor.state';
import { useTranslations } from '~/modules/office/i18n/useTranslations';

export interface OfficeEditorProps {
  fileId: string;
  height?: string;
  width?: string;
  onReady?: () => void;
  onError?: (error: Error) => void;
  permission?: 'Read' | 'Write';
  className?: string;
}

export const OfficeEditor: React.FC<OfficeEditorProps> = ({
  fileId,
  onReady,
  onError,
  permission = 'Write',
  className = '',
}) => {
  const t = useTranslations();
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [session, setSession] = useAtom($currentEditingSession);
  const [_isReady, setIsReady] = useAtom($isEditorReady);
  const isDocumentLoaded = useAtom($isDocumentLoaded)[0];
  const startEditingSession = useSetAtom($startEditingSession);
  const handleCollaboraMessage = useSetAtom($handleCollaboraMessage);
  const sendCollaboraMessage = useSetAtom($sendCollaboraMessage);
  const [error, setError] = React.useState<string | null>(null);

  // Initialize editing session
  React.useEffect(() => {
    const initSession = async () => {
      try {
        setSession({
          fileId,
          editUrl: '',
          collaboraUrl: '',
          token: '',
          isLoading: true,
        });

        const sessionData = await startEditingSession({ fileId, permission });

        setSession({
          fileId,
          editUrl: sessionData.editUrl,
          collaboraUrl: sessionData.collaboraUrl,
          token: sessionData.token.token,
          isLoading: false,
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t.office.editor.failedToStartSession;
        setError(errorMessage);
        setSession(null);
        onError?.(err instanceof Error ? err : new Error(errorMessage));
      }
    };

    initSession();

    // Cleanup on unmount
    return () => {
      setSession(null);
      setIsReady(false);
    };
  }, [
    fileId,
    permission,
    setSession,
    setIsReady,
    startEditingSession,
    onError,
  ]);

  // Handle postMessage events
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      handleCollaboraMessage(event);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleCollaboraMessage]);

  // Notify when ready
  React.useEffect(() => {
    if (isDocumentLoaded && onReady) {
      onReady();
    }
  }, [isDocumentLoaded, onReady]);

  // Send UI defaults when iframe loads
  const handleIframeLoad = React.useCallback(() => {
    // Send UI defaults to customize Collabora interface
    setTimeout(() => {
      sendCollaboraMessage({
        type: 'Host_PostmessageReady',
        data: {},
      });

      // Configure UI elements
      sendCollaboraMessage({
        type: 'Action_SetUIDefaults',
        data: {
          uiDefaults: {
            UIMode: 'notebookbar', // or 'classic'
            ShowSidebar: false,
            ShowStatusBar: true,
            ShowRuler: true,
          },
        },
      });
    }, 1000);
  }, [sendCollaboraMessage]);

  if (error) {
    return (
      <div className={cn('flex items-center justify-center', className)}>
        <div className="flex flex-col items-center space-y-4">
          <p className="text-lg text-failure/50">
            {t.office.editor.errorLoadingDocument}
          </p>
          <p className="text-neutral/60">{error}</p>
        </div>
      </div>
    );
  }

  if (!session || session.isLoading) {
    return <Loading isLoading={true} className={className} />;
  }

  return (
    <div className={cn('@OfficeEditor', 'relative', className)}>
      <iframe
        ref={iframeRef}
        id="collabora-iframe"
        src={session.editUrl}
        style={{
          border: 'none',
          display: 'block',
        }}
        className="size-full"
        allow="clipboard-read; clipboard-write"
        onLoad={handleIframeLoad}
      />
      {!isDocumentLoaded && (
        <div className="absolute inset-0 z-10">
          <Loading isLoading={true} className="size-full" />
        </div>
      )}
    </div>
  );
};
